import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import SlidePanel from '../shared/SlidePanel';
import { productHasVariants } from '../shared/CartVariantPicker';
import { productService } from '../../_services/product.service';
import { paymentService } from '../../_services/payment.service';
import { voucherService } from '../../_services/voucherService';
import { hairdresserService } from '../../_services/hairdresser.service';
import { newServicesAction, bookingActions, paymentActions } from '../../_actions';
import { voucherActions } from '../../_actions/voucher.actions';
import ProductGrid from './components/ProductGrid';
import CartPanel from './components/CartPanel';
import POSHeader from './components/POSHeader';
import NotificationBell from '../notifications/NotificationBell';
import LocationPicker from '../multiLocation/LocationPicker';
import StaffSelector from './components/StaffSelector';
import PaymentScreen from '../checkout/screens/PaymentScreen';
import TipScreen from '../checkout/screens/TipScreen';
import DiscountScreen from '../checkout/screens/DiscountScreen';
import CodeScreen from '../discounts/till/CodeScreen';
import { promoFor } from '../discounts/till/tillCode';
import { holdCode, clientKeyOf } from '../discounts/discountStore';
import VoucherScreen from '../checkout/screens/VoucherScreen';
import LoyaltyScreen from '../checkout/screens/LoyaltyScreen';
import PaymentLinkScreen from '../checkout/screens/PaymentLinkScreen';
import CardReaderScreen from '../checkout/screens/CardReaderScreen';
import ClientPickerScreen from './screens/ClientPickerScreen';
import CompleteScreen from '../checkout/screens/CompleteScreen';
import VoucherAmountPicker from '../gift-cards-v2/VoucherAmountPicker';
import VoucherAmountFields from '../gift-cards-v2/VoucherAmountFields';
import usePOSState from './hooks/usePOSState';
import useStripeTerminal from '../checkout/hooks/useStripeTerminal';
import useTapToPayTerminal from '../checkout/hooks/useTapToPayTerminal';
import TapToPayScreen from '../checkout/screens/TapToPayScreen';
import toast from '../shared/Toast';
import { round2 } from '../checkout/utils/checkoutCalculations';
import { startPaymentIntent } from '../checkout/utils/paymentStartError';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import '../new-service/CatalogueTopNav.css';
import './POS.css';

/**
 * POS items have a flat shape (no per-item discount, only cart-level state.discount).
 * Spread the cart discount proportionally so each item carries its sold-at unit price
 * downstream — matches what the diary checkout does, keeps commission + booking
 * record consistent with what the customer paid.
 */
const posUnitPriceWithDiscount = (item, discount, denom) => {
  // A discount code's saving sits on the line itself (never with a cart discount)
  if (item.promoOff) return round2(Math.max(0, (parseFloat(item.price) || 0) - item.promoOff));
  const price = parseFloat(item.price) || 0;
  if (!discount || !discount.type || price <= 0) return price;
  if (discount.type === 'percentage') {
    return round2(price * (1 - (discount.value || 0) / 100));
  }
  if (discount.type === 'fixed' && denom > 0) {
    const cap = Math.min(discount.value || 0, denom);
    const qty = item.quantity || 1;
    const itemTotalShare = (price * qty) / denom;
    return round2(price - (cap * itemTotalShare) / qty);
  }
  return price;
};
// The till's lines as the code check reads them (the checkout's shape)
const tillItems = (items) => (items || []).map((i) => ({
  type: i.type,
  commonServiceId: i.type === 'service' ? i.serviceId : null,
  serviceId: i.serviceId || i.productId || i.voucherId,
  staffId: i.staffId,
  staffName: i.staffName,
  originalPrice: parseFloat(i.price) || 0,
  quantity: i.quantity || 1,
}));
// A code's per-line discounts as the till's per-unit saving on each line
const offsFrom = (lineDiscounts, items) => {
  const out = {};
  Object.keys(lineDiscounts || {}).forEach((k) => {
    const d = lineDiscounts[k];
    const price = parseFloat((items[k] || {}).price) || 0;
    out[k] = d.type === 'percentage' ? round2(price * (d.value / 100)) : round2(Math.min(d.value, price));
  });
  return out;
};
const posSubtotal = (items) =>
  round2((items || []).reduce((s, i) => s + (parseFloat(i.price) || 0) * (i.quantity || 1), 0));

/**
 * POSPage - Main POS/Quick Checkout page.
 * Split layout: Product Grid (left) + Cart (right).
 * Completely independent from existing checkout.
 */
const POSPage = (props) => {
  const [state, actions] = usePOSState();
  // Load ALL of the picked client's loyalty cards (walk-in has none). Not just
  // redeemable ones: the till shows the reward button either way, with stamp
  // progress when there is nothing to redeem yet, matching the diary checkout.
  // LoyaltyScreen filters down to redeemable cards itself.
  const posClientId = state.client?._id;
  useEffect(() => {
    if (!posClientId || posClientId === '64f5c7789a069cda0188fea3') { actions.setLoyaltyRewards([]); return; }
    marketingBackend.loyaltyClientCards(posClientId)
      .then((res) => actions.setLoyaltyRewards((res && res.cards) || []))
      .catch(() => actions.setLoyaltyRewards([]));
  }, [posClientId]); // eslint-disable-line react-hooks/exhaustive-deps
  const terminal = useStripeTerminal();
  const tapToPay = useTapToPayTerminal();
  const [cardReaderList, setCardReaderList] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState('products');
  const [activeSubCategory, setActiveSubCategory] = useState('all');
  const [cartOpen, setCartOpen] = useState(false); // mobile cart sheet
  const cartSlidePanelRef = useRef(null);
  const closeCartWithAnimation = useCallback(() => {
    if (cartSlidePanelRef.current && cartSlidePanelRef.current.handleClose) {
      cartSlidePanelRef.current.handleClose();
    } else {
      setCartOpen(false);
    }
  }, []);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [lastAddedId, setLastAddedId] = useState(null);
  const [cartPulse, setCartPulse] = useState(false);
  const [pickerVoucher, setPickerVoucher] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState({
    staffId: localStorage.getItem('userId') || '',
    staffName: '',
  });

  // Track mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const currencySymbol = props.user?.userInfo?.hairdresser?.currencySymbol || '£';
  const loggedInStaffName = props.user?.userInfo?.name || '';
  const salonName = props.user?.userInfo?.hairdresser?.salon?.salonName || '';
  // Named when the server refuses a card payment for this stylist.
  const paymentStaffName = selectedStaff?.staffName || '';

  // Set default staff name once user info loads
  useEffect(() => {
    if (loggedInStaffName && !selectedStaff.staffName) {
      setSelectedStaff(prev => ({ ...prev, staffName: loggedInStaffName }));
    }
  }, [loggedInStaffName]);

  // Load card readers (for Beu Terminal card reader flow)
  const loadCardReaders = useCallback(async () => {
    try {
      const response = await props.dispatch(paymentActions.cardReaderList());
      const result = response?.data?.responseData?.result || {};
      setCardReaderList(result.data || []);
    } catch (e) { /* silent */ }
  }, [props.dispatch]);

  useEffect(() => {
    loadCardReaders();
  }, [loadCardReaders]);

  // Load products, categories, services, and vouchers on mount
  useEffect(() => {
    const salonId = localStorage.getItem('salonId');

    Promise.all([
      productService.getProductsList({ salonId: salonId || '', search: '' }),
      productService.listProductCategories(),
      props.dispatch(newServicesAction.listServicesData({})),
      voucherService.listVoucherCatalogs({}).catch(() => null),
      props.dispatch(newServicesAction.listHairDresserData({})).catch(() => null),
    ])
      .then(([productRes, categoryRes, serviceRes, voucherRes, staffRes]) => {
        // Products
        const productData = productRes?.data?.data || productRes?.data?.responseData || {};
        let productList = productData.products || productData.productList || productData.result || [];
        if (productList.length === 0 && Array.isArray(productData)) {
          productList = productData.reduce((acc, cat) => [...acc, ...(cat.products || [])], []);
        }
        // Filter out deleted/inactive products
        productList = (Array.isArray(productList) ? productList : []).filter(p =>
          !p.isDeleted && !p.deleted && p.status !== 'deleted' && p.isActive !== false
        );
        setProducts(productList);

        // Categories
        const catData = categoryRes?.data?.data || categoryRes?.data?.responseData || {};
        const catList = catData.categories || catData.result || [];
        setCategories(Array.isArray(catList) ? catList : []);

        // Services (salon-wide, grouped by service category)
        const svcCategories = serviceRes?.listServices || [];
        setServices(Array.isArray(svcCategories) ? svcCategories : []);

        // Voucher catalogs
        if (voucherRes) {
          const vData = voucherRes?.data?.data || voucherRes?.data?.responseData?.data || {};
          const vList = vData.catalogs || vData.result || [];
          setVouchers(Array.isArray(vList) ? vList : []);
        }

        // Staff list
        if (staffRes) {
          const sList = staffRes?.listHairdresser || [];
          setStaffList(Array.isArray(sList) ? sList : []);
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  // Re-fetch services when selected staff changes (API filters server-side)
  useEffect(() => {
    if (!selectedStaff?.staffId) return;
    props.dispatch(newServicesAction.listServicesData({ hairdresserId: selectedStaff.staffId }))
      .then(serviceRes => {
        const svcCategories = serviceRes?.listServices || [];
        setServices(Array.isArray(svcCategories) ? svcCategories : []);
      })
      .catch(() => {});
  }, [selectedStaff?.staffId]);

  // Animation feedback trigger
  const triggerAddFeedback = useCallback((id) => {
    setLastAddedId(id);
    setCartPulse(true);
    setTimeout(() => setLastAddedId(null), 500);
    setTimeout(() => setCartPulse(false), 400);
  }, []);

  // Add product to cart
  // A line keeps the price it was added at. When the product list loads again
  // (after a price change in the catalogue, say), bring untouched product
  // lines up to date. Never once payment has started, never a price someone
  // typed in, never a variant line (its price comes from the variant).
  useEffect(() => {
    if (!products.length || state.transactions.length) return;
    state.items.forEach((item, i) => {
      if (item.type !== 'product' || item.variantId || item.priceEdited) return;
      const p = products.find(x => (x._id || x.id) === item.productId);
      if (!p) return;
      const price = parseFloat(p.retailPrice != null && p.retailPrice !== '' ? p.retailPrice : p.price);
      if (!Number.isFinite(price) || price === item.price) return;
      actions.updateItem(i, { price, stockPrice: parseFloat(p.stockPrice) || 0 });
    });
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddProduct = useCallback((product) => {
    const pid = product._id || product.id;
    const isVariantProduct = productHasVariants(product);
    // Two variants of the same product are two different things to sell, so
    // they must be separate lines. An unresolved variant line never merges —
    // otherwise tapping Tshirt twice would stack into one line you then can't
    // split into a Stone and a Black.
    const existingIndex = isVariantProduct ? -1 : state.items.findIndex(
      item => item.type === 'product' && item.productId === pid && !item.variantId
    );
    if (existingIndex >= 0) {
      actions.setQuantity(existingIndex, (state.items[existingIndex].quantity || 1) + 1);
      triggerAddFeedback(pid);
      return;
    }

    actions.addItem({
      type: 'product',
      productId: pid,
      name: product.productName || product.name || '',
      price: parseFloat(product.retailPrice || product.price) || 0,
      stockPrice: parseFloat(product.stockPrice) || 0,
      commissionEnable: product.commissionEnable || 0,
      quantity: 1,
      image: product.images?.[0]?.url || null,
      categoryId: product.categoryId,
      barcode: product.barcode || '',
      trackStock: product.trackStock,
      stockQty: product.quantity || 0,
      // Carried so the line can be resolved in the sidebar — there is no
      // product page at the till to pick a variant on.
      options: product.options || [],
      variants: product.variants || [],
      variantId: null,
      variantLabel: '',
      staffId: selectedStaff.staffId,
      staffName: selectedStaff.staffName,
    });
    triggerAddFeedback(pid);
  }, [state.items, actions, triggerAddFeedback, selectedStaff]);

  // Add service to cart
  const handleAddService = useCallback((service) => {
    const sid = service.commonServiceId || service.serviceId || service._id;
    actions.addItem({
      type: 'service',
      serviceId: sid,
      name: service.serviceName || '',
      price: parseFloat(service.servicePrice) || 0,
      quantity: 1,
      staffId: selectedStaff.staffId,
      staffName: selectedStaff.staffName,
      diaryColour: service.diaryColour || null,
      serviceTime: service.serviceTime || 0,
      categoryName: service._categoryName || '',
    });
    triggerAddFeedback(sid);
  }, [actions, selectedStaff, triggerAddFeedback]);

  // Tapping a voucher opens the amount picker (denomination / custom + promo)
  // before it's added to the cart.
  const handleAddVoucher = useCallback((voucher) => {
    setPickerVoucher(voucher);
  }, []);

  // The picker returns the chosen face value + the (promo) charge collected.
  const handleVoucherAmountConfirmed = useCallback(({ faceValue, charge }) => {
    const voucher = pickerVoucher;
    const vid = voucher.id || voucher._id;
    actions.addItem({
      type: 'voucher',
      voucherId: vid,
      name: voucher.name || 'Gift Voucher',
      price: charge,        // collected at the till (promo price when discounted)
      faceValue,            // redeemable value issued on the card
      quantity: 1,
      staffId: selectedStaff.staffId,
      staffName: selectedStaff.staffName,
    });
    setPickerVoucher(null);
    triggerAddFeedback(vid);
  }, [pickerVoucher, actions, triggerAddFeedback, selectedStaff]);

  const handleTypeChange = useCallback((type) => {
    setActiveType(type);
    setActiveSubCategory('all');
  }, []);

  // Build a set of category IDs that have at least one product
  const activeCategoryIds = useMemo(() => {
    const ids = new Set();
    products.forEach(p => {
      const catId = p.categoryId || p.category?._id || (typeof p.category === 'string' ? p.category : null);
      if (catId) ids.add(catId);
    });
    return ids;
  }, [products]);

  // Only show categories that have products
  const visibleCategories = useMemo(() => {
    return categories.filter(c => {
      const catId = c._id || c.categoryId || c.id;
      return catId && activeCategoryIds.has(catId);
    });
  }, [categories, activeCategoryIds]);

  // Filter products by search and sub-category
  const filteredProducts = useMemo(() => {
    if (activeType !== 'products') return [];
    return products.filter(p => {
      const name = (p.productName || p.name || '').toLowerCase();
      const sku = (p.sku || '').toLowerCase();
      const barcode = (p.barcode || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || name.includes(q) || sku.includes(q) || barcode.includes(q);
      const pCatId = p.categoryId || p.category?._id || (typeof p.category === 'string' ? p.category : null);
      const matchesCategory = activeSubCategory === 'all' || pCatId === activeSubCategory;
      return matchesSearch && matchesCategory;
    });
  }, [activeType, activeSubCategory, products, searchQuery]);

  // Build service categories with colours and search filtering
  const catColours = useMemo(() => ['#D6D5EC', '#C8EEEE', '#f5dabf', '#d4edda', '#fde8e8', '#dce6f7', '#f7e6dc', '#e8dff5'], []);

  const serviceCategories = useMemo(() => {
    if (activeType !== 'services') return [];
    const q = searchQuery.toLowerCase();
    return services.map((cat, catIdx) => {
      const catName = cat.categoryName || '';
      const catId = cat._id || `svc-cat-${catIdx}`;
      const catFallback = catColours[catIdx % catColours.length];
      const items = (cat.serviceList || cat.services || [])
        .filter(svc => !q || (svc.serviceName || '').toLowerCase().includes(q))
        .map(svc => {
          const staffColour = svc.hairdresserService?.[0]?.diaryColour;
          return { ...svc, diaryColour: svc.diaryColour || staffColour || catFallback };
        });
      return { categoryName: catName, categoryId: catId, colour: catFallback, items };
    }).filter(cat => cat.items.length > 0);
  }, [activeType, services, searchQuery, catColours]);

  // If a service sub-category is selected, filter to just that category
  const filteredServiceCategories = useMemo(() => {
    if (activeSubCategory === 'all') return serviceCategories;
    return serviceCategories.filter(cat => cat.categoryId === activeSubCategory);
  }, [activeSubCategory, serviceCategories]);

  // Featured services (salon-level)
  const featuredServices = useMemo(() => {
    if (activeType !== 'services') return [];
    const q = searchQuery.toLowerCase();
    const result = [];
    services.forEach((cat, catIdx) => {
      const catFallback = catColours[catIdx % catColours.length];
      (cat.serviceList || cat.services || []).forEach(svc => {
        if (svc.isFeaturedForSalon) {
          if (q && !(svc.serviceName || '').toLowerCase().includes(q)) return;
          const staffColour = svc.hairdresserService?.[0]?.diaryColour;
          result.push({ ...svc, diaryColour: svc.diaryColour || staffColour || catFallback });
        }
      });
    });
    result.sort((a, b) => (a.featuredOrderSalon || 0) - (b.featuredOrderSalon || 0));
    return result;
  }, [activeType, services, searchQuery, catColours]);

  // Service sub-category tab list
  const serviceCategoryTabs = useMemo(() => {
    return serviceCategories.map(cat => ({ id: cat.categoryId, label: cat.categoryName }));
  }, [serviceCategories]);

  // Filter vouchers by search
  const filteredVouchers = useMemo(() => {
    if (activeType !== 'vouchers') return [];
    return vouchers.filter(v => {
      if (!searchQuery) return true;
      return (v.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [activeType, vouchers, searchQuery]);

  // Build temporary booking params for payment link creation
  const buildCheckoutBookingParams = useCallback((paymentType) => {
    const salonId = localStorage.getItem('salonId');
    const hairdresserId = selectedStaff?.staffId || localStorage.getItem('userId');
    const clientId = state.client?._id || '64f5c7789a069cda0188fea3'; // walk-in fallback

    // Spread cart-level discount proportionally so the booking record stores
    // the actual sold-at unit price (drives backend commission calc + cashup).
    const denom = posSubtotal(state.items);

    const servicesBooked = state.items
      .filter(i => i.type === 'service')
      .map(i => ({
        serviceId: i.serviceId,
        serviceName: i.name,
        servicePrice: posUnitPriceWithDiscount(i, state.discount, denom),
        serviceTime: i.serviceTime || 30,
        depositPrice: 0,
      }));

    const products = state.items
      .filter(i => i.type === 'product')
      .map(i => ({
        _id: i.productId,
        productName: i.name,
        variantId: i.variantId || undefined,
        variantLabel: i.variantLabel || undefined,
        quantity: i.quantity || 1,
        retailPrice: posUnitPriceWithDiscount(i, state.discount, denom),
        stockPrice: i.stockPrice || 0,
        commissionEnable: i.commissionEnable || 0,
        type: 'product',
      }));

    const vouchers = state.items
      .filter(i => i.type === 'voucher')
      .map(i => ({
        _id: i.voucherId,
        voucherPrice: posUnitPriceWithDiscount(i, state.discount, denom),  // collected (promo) price
        faceValue: i.faceValue != null ? i.faceValue : posUnitPriceWithDiscount(i, state.discount, denom),
        voucherCode: i.voucherCode || '',
      }));

    const transaction = JSON.stringify(state.transactions.map(t => ({
      paymentType: t.paymentType,
      amount: t.amount,
      ...(t.voucherId ? { voucherId: t.voucherId, voucherCode: t.voucherCode } : {}),
      // Keep the scheme linkage on loyalty comp rows (paymentType 16) so the BE
      // (bookingCheckout) validates/enriches them and consumes on complete.
      ...(t.loyaltySchemeId ? { loyaltySchemeId: t.loyaltySchemeId } : {}),
    })));

    return {
      salonId,
      hairdresserId,
      userId: clientId,
      bookingDate: new Date().toISOString().split('T')[0],
      bookingSlot: new Date().toTimeString().split(' ')[0].slice(0, 5),
      servicesBooked,
      products,
      vouchers,
      isCheckout: 1,
      bookingStatus: 1,
      totalAmount: state.total,
      transaction,
      selectedPaymentType: paymentType,
      // A discount code, once the server checks codes (it refuses fields it
      // doesn't know): which code, and its saving on each line
      ...(state.promo && state.promo.serverChecked ? { promo: promoFor(state.promo, tillItems(state.items)) } : {}),
    };
  }, [state.items, state.total, state.client, state.transactions, state.promo, selectedStaff]);

  // Persist completed sale to backend when checkout completes.
  // For cash/card/other flows only — payment link/Klarna/Clearpay already created
  // a backend booking in createPaymentLinkFlow and the webhook marked it complete.
  const saveCompletedRef = useRef(false);
  useEffect(() => {
    if (!state.isComplete) {
      saveCompletedRef.current = false;
      return;
    }
    if (saveCompletedRef.current) return;
    if (state.bookingId) return; // backend booking already created (payment link or card reader) — already persisted
    if (state.transactions.length === 0) return;

    saveCompletedRef.current = true;
    const primary = state.transactions.reduce(
      (a, b) => (b.amount > a.amount ? b : a),
      state.transactions[0],
    );
    const params = buildCheckoutBookingParams(primary?.paymentType || 3);
    params.bookingStatus = 4;

    const linkVoucherRedemptions = async (bookingId) => {
      if (!bookingId) return;
      const voucherTxns = state.transactions.filter(t => t.paymentType === 11 && t.voucherId);
      for (const vt of voucherTxns) {
        try {
          await props.dispatch(voucherActions.updateVoucherRedemption(
            vt.voucherId,
            vt.redemptionIndex || 0,
            bookingId,
          ));
        } catch (e) { /* non-critical — redemption already recorded */ }
      }
    };

    props
      .dispatch(bookingActions.createNewCheckoutBooking(params))
      .then(res => {
        const bookingId = res?.data?.responseData?.result?._id || res?.data?.responseData?._id;
        if (bookingId) actions.setBookingId(bookingId);
        // The code's use, held on this sale (the device stand-in needs telling)
        if (bookingId && state.promo) {
          holdCode({
            salonId: localStorage.getItem('salonId'),
            code: state.promo.code,
            bookingId,
            clientKey: clientKeyOf({ phone: state.client && (state.client.phoneNumber || state.client.phone), userId: state.client && state.client._id }),
            amount: state.promo.amount,
            total: state.total,
            channel: 'salon',
          }).catch(() => {});
        }
        return linkVoucherRedemptions(bookingId);
      })
      .catch(() => {
        toast.error('Sale completed locally but failed to save to finance');
      });
  }, [state.isComplete]);

  // Helper: create temp booking + payment link, then show QR screen
  const createPaymentLinkFlow = async (paymentType, linkAction, linkType, amount) => {
    actions.setProcessing(true);
    try {
      const params = buildCheckoutBookingParams(paymentType);
      const bookingRes = await props.dispatch(bookingActions.createNewCheckoutBooking(params));
      const bookingId = bookingRes?.data?.responseData?.result?._id || bookingRes?.data?.responseData?._id;
      if (!bookingId) throw new Error('No booking ID');
      actions.setBookingId(bookingId);

      const linkRes = await props.dispatch(linkAction({ bookingId }));
      const linkData = linkRes?.data?.responseData || linkRes?.data?.data || {};

      actions.setPaymentLinkData({ ...linkData, amount, bookingId }, linkType);
      actions.setScreen('paymentLink');
    } catch (e) {
      toast.error('Failed to create payment link');
    }
    actions.setProcessing(false);
  };

  // POS payment methods hook (matches diary pmHook interface)
  const posPmHook = {
    processSimplePayment: (type, amount, label) => {
      actions.addTransaction({ paymentType: type, amount, label });
    },
    processPaymentLink: (amount) => createPaymentLinkFlow(12, paymentActions.createPaymentLink, 'payment', amount),
    processKlarna: (amount) => createPaymentLinkFlow(13, paymentActions.createKlarnaPaymentLink, 'klarna', amount),
    processClearpay: (amount) => createPaymentLinkFlow(14, paymentActions.createClearpayPaymentLink, 'clearpay', amount),
    checkPaymentLinkStatus: async () => {
      const bookingId = state.paymentLinkData?.bookingId;
      if (!bookingId) {
        console.warn('POS checkPaymentLinkStatus: no bookingId', state.paymentLinkData);
        return {};
      }
      try {
        const res = await paymentService.checkPaymentLinkStatus({ bookingId });
        const data = res?.data?.responseData || res?.data?.data || {};
        return data;
      } catch (e) {
        console.error('POS checkPaymentLinkStatus error:', e);
        return {};
      }
    },
    handlePaymentLinkComplete: async (booking) => {
      const amount = state.paymentLinkData?.amount || state.balanceRemaining;
      const typeMap = { payment: 12, klarna: 13, clearpay: 14 };
      const labelMap = { payment: 'Payment Link', klarna: 'Klarna', clearpay: 'Clearpay' };
      actions.addTransaction({
        paymentType: typeMap[state.paymentLinkType] || 12,
        amount,
        label: labelMap[state.paymentLinkType] || 'Payment Link',
      });
      // Link voucher redemptions to the booking created in createPaymentLinkFlow
      const bookingId = state.bookingId;
      if (bookingId) {
        const voucherTxns = state.transactions.filter(t => t.paymentType === 11 && t.voucherId);
        for (const vt of voucherTxns) {
          try {
            await props.dispatch(voucherActions.updateVoucherRedemption(
              vt.voucherId,
              vt.redemptionIndex || 0,
              bookingId,
            ));
          } catch (e) { /* non-critical */ }
        }
      }
      actions.setPaymentLinkData(null, null);
    },
    closePaymentLinkModal: () => {
      actions.setPaymentLinkData(null, null);
    },
    paymentLinkData: state.paymentLinkData,
    paymentLinkType: state.paymentLinkType,
    cardReaderList,
    loadCardReaders,
    paymentMethodInfo: null,
  };

  // POS payment methods — fetched per selected staff member, mirrors the
  // diary checkout flow. cardOnFile stays disabled because POS sales aren't
  // tied to a known client with a saved payment method.
  const [posPaymentMethods, setPosPaymentMethods] = useState({});
  useEffect(() => {
    if (!selectedStaff?.staffId) return;
    hairdresserService.getPaymentMethods({ hairdresserId: selectedStaff.staffId })
      .then(response => {
        const data = response?.data?.data || response?.data?.responseData || {};
        const methods = data.paymentMethods || data;
        setPosPaymentMethods({ ...methods, cardOnFile: false });
      })
      .catch(() => {});
  }, [selectedStaff?.staffId]);

  // Shared cart screen renderer
  // Desktop: the chosen voucher's amount step renders inside the right cart
  // panel (inline), not a floating sheet. Mobile uses the bottom sheet.
  const renderVoucherAmountStep = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={() => setPickerVoucher(null)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">{pickerVoucher.name || 'Gift Voucher'}</h2>
      </div>
      <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
        <VoucherAmountFields
          voucher={pickerVoucher}
          currency={currencySymbol}
          onConfirm={handleVoucherAmountConfirmed}
        />
      </div>
    </div>
  );

  const renderCartScreen = () => {
    switch (state.screen) {
      case 'payment':
        return <PaymentScreen
          state={{ ...state, currencySymbol, paymentMethods: posPaymentMethods }}
          actions={actions}
          dispatch={props.dispatch}
          onBack={() => actions.setScreen('summary')}
          paymentMethods={posPmHook}
          tapToPay={tapToPay}
        />;
      case 'tip':
        return <TipScreen state={{ ...state, discountTotal: state.discountAmount || 0 }} actions={actions} onBack={() => actions.setScreen('summary')} currencySymbol={currencySymbol} />;
      case 'code':
        return <CodeScreen
          items={tillItems(state.items)}
          promo={state.promo}
          client={state.client}
          salonId={localStorage.getItem('salonId')}
          subtotal={state.subtotal}
          currencySymbol={currencySymbol}
          onApply={(promo, lineDiscounts) => actions.applyPromo(promo, offsFrom(lineDiscounts, state.items))}
          onRemove={actions.removePromo}
          onBack={() => actions.setScreen('summary')}
        />;
      case 'discount':
        return <DiscountScreen
          state={{ ...state, cartDiscount: state.discount }}
          actions={{ ...actions, setCartDiscount: actions.setDiscount }}
          onBack={() => actions.setScreen('summary')}
          currencySymbol={currencySymbol}
        />;
      case 'cardReader':
        return <CardReaderScreen
          cardReaders={cardReaderList}
          terminal={terminal}
          amount={state.balanceRemaining}
          dispatch={props.dispatch}
          onReadersRefresh={loadCardReaders}
          onBack={() => { if (!terminal.isProcessing) actions.setScreen('payment'); }}
          onPaymentComplete={async () => {
            const amount = state.balanceRemaining;
            // Ensure a backend booking exists — create one if this is the first electronic payment
            let bookingId = state.bookingId;
            if (!bookingId) {
              try {
                const createParams = buildCheckoutBookingParams(5);
                const bookingRes = await props.dispatch(bookingActions.createNewCheckoutBooking(createParams));
                bookingId = bookingRes?.data?.responseData?.result?._id || bookingRes?.data?.responseData?._id;
                if (!bookingId) throw new Error('No booking ID');
                actions.setBookingId(bookingId);
              } catch (e) {
                toast.error('Failed to create booking for card reader');
                return;
              }
            }
            // Bake cart discount in so backend commission calc + booking record
            // reflect the actual sold-at price.
            const denomCR = posSubtotal(state.items);
            const servicesToBook = state.items
              .filter(i => i.type === 'service')
              .map(i => ({ id: i.serviceId, price: posUnitPriceWithDiscount(i, state.discount, denomCR) }));
            const productIds = state.items
              .filter(i => i.type === 'product')
              .map(i => ({
                id: i.productId,
                variantId: i.variantId || undefined,
                quantity: i.quantity || 1,
                retailPrice: posUnitPriceWithDiscount(i, state.discount, denomCR)
              }));
            const response = await startPaymentIntent(() => props.dispatch(paymentActions.paymentIntents({
              bookingId,
              servicesToBook,
              productIds,
              voucherIds: [],
              amount,
              selectedPaymentType: 5,
            })), paymentStaffName);
            if (response?.data?.statusCode === 1) {
              const clientSecret = response.data.responseData?.result?.client_secret;
              const paymentIntentId = response.data.responseData?.result?.id;
              const result = await terminal.processPayment({
                clientSecret,
                paymentIntentId,
                bookingId,
                servicesToBook,
                voucherIds: [],
                onCapture: (params) => props.dispatch(paymentActions.paymentIntentsCapture(params)).then(r => r),
              });
              if (result?.success) {
                actions.addTransaction({ paymentType: 5, amount: result.amount || amount, label: 'Card Reader' });
                // Link any voucher redemptions accumulated in this session to the booking
                const voucherTxns = state.transactions.filter(t => t.paymentType === 11 && t.voucherId);
                for (const vt of voucherTxns) {
                  try {
                    await props.dispatch(voucherActions.updateVoucherRedemption(
                      vt.voucherId,
                      vt.redemptionIndex || 0,
                      bookingId,
                    ));
                  } catch (e) { /* non-critical */ }
                }
                setTimeout(() => actions.markComplete(), 100);
              }
            }
          }}
          currencySymbol={currencySymbol}
        />;
      case 'tapToPay': {
        // Tap to Pay flow for POS — mirrors cardReader pattern but uses
        // the native iOS Stripe Terminal SDK Tap-to-Pay reader. POS sales
        // don't have an existing booking until first electronic payment,
        // so we lazy-create one via createNewCheckoutBooking, same as the
        // cardReader case above.
        const stripeLocationId = selectedStaff?.stripeLocationId
          || localStorage.getItem('stripeLocationId');
        return <TapToPayScreen
          terminal={tapToPay}
          amount={state.balanceRemaining}
          locationId={stripeLocationId}
          currencySymbol={currencySymbol}
          onBack={() => { if (!tapToPay.isProcessing) actions.setScreen('payment'); }}
          onPaymentComplete={async () => {
            const amount = state.balanceRemaining;
            let bookingId = state.bookingId;
            if (!bookingId) {
              try {
                const createParams = buildCheckoutBookingParams(15);
                const bookingRes = await props.dispatch(bookingActions.createNewCheckoutBooking(createParams));
                bookingId = bookingRes?.data?.responseData?.result?._id || bookingRes?.data?.responseData?._id;
                if (!bookingId) throw new Error('No booking ID');
                actions.setBookingId(bookingId);
              } catch (e) {
                toast.error('Failed to create booking for Tap to Pay');
                return;
              }
            }
            const denomTTP = posSubtotal(state.items);
            const servicesToBook = state.items
              .filter(i => i.type === 'service')
              .map(i => ({ id: i.serviceId, price: posUnitPriceWithDiscount(i, state.discount, denomTTP) }));
            const productIds = state.items
              .filter(i => i.type === 'product')
              .map(i => ({
                id: i.productId,
                variantId: i.variantId || undefined,
                quantity: i.quantity || 1,
                retailPrice: posUnitPriceWithDiscount(i, state.discount, denomTTP)
              }));
            const response = await startPaymentIntent(() => props.dispatch(paymentActions.paymentIntents({
              bookingId,
              servicesToBook,
              productIds,
              voucherIds: [],
              amount,
              selectedPaymentType: 15,
            })), paymentStaffName);
            if (response?.data?.statusCode === 1) {
              const clientSecret = response.data.responseData?.result?.client_secret;
              const paymentIntentId = response.data.responseData?.result?.id;
              const result = await tapToPay.processPayment({
                clientSecret,
                paymentIntentId,
                bookingId,
                servicesToBook,
                voucherIds: [],
                onCapture: (params) => props.dispatch(paymentActions.paymentIntentsCapture(params)).then(r => r),
              });
              if (result?.success) {
                actions.addTransaction({ paymentType: 15, amount: result.amount || amount, label: 'Tap to Pay' });
                // Link any voucher redemptions accumulated in this session
                const voucherTxns = state.transactions.filter(t => t.paymentType === 11 && t.voucherId);
                for (const vt of voucherTxns) {
                  try {
                    await props.dispatch(voucherActions.updateVoucherRedemption(
                      vt.voucherId,
                      vt.redemptionIndex || 0,
                      bookingId,
                    ));
                  } catch (e) { /* non-critical */ }
                }
                setTimeout(() => actions.markComplete(), 100);
              }
            }
          }}
        />;
      }
      case 'paymentLink':
        return <PaymentLinkScreen
          paymentLinkData={posPmHook.paymentLinkData}
          paymentType={posPmHook.paymentLinkType}
          onConfirm={(booking) => posPmHook.handlePaymentLinkComplete(booking)}
          onClose={() => { posPmHook.closePaymentLinkModal(); actions.setScreen('payment'); }}
          onCheckStatus={posPmHook.checkPaymentLinkStatus}
          currencySymbol={currencySymbol}
          clientEmail={state.client?.email || ''}
          clientName={state.client?.name || ''}
        />;
      case 'clientPicker':
        return <ClientPickerScreen
          onSelect={(client) => {
            actions.setClient({ _id: client._id, name: client.name, email: client.email, phone: client.phoneNumber });
            actions.setScreen('summary');
          }}
          onBack={() => actions.setScreen('summary')}
        />;
      case 'voucher':
        return <VoucherScreen
          state={state}
          actions={actions}
          dispatch={props.dispatch}
          onBack={() => actions.setScreen('payment')}
          currencySymbol={currencySymbol}
        />;
      case 'loyalty':
        // LoyaltyScreen reads state.lineItems ({ type, name, originalPrice });
        // POS carries flat `items` with `.price`, so adapt them here.
        return <LoyaltyScreen
          state={{ ...state, lineItems: state.items.map((it) => ({ type: it.type, name: it.name, originalPrice: it.price })) }}
          actions={actions}
          onBack={() => actions.setScreen('payment')}
          currencySymbol={currencySymbol}
        />;
      case 'complete':
        return <CompleteScreen
          state={{ ...state, discountTotal: state.discountAmount || 0, staffBreakdown: [], sellerUserId: selectedStaff?.staffId || null }}
          onDone={() => actions.reset()}
          currencySymbol={currencySymbol}
          dispatch={props.dispatch}
        />;
      default:
        return <CartPanel state={state} actions={actions} currencySymbol={currencySymbol} onClose={closeCartWithAnimation} />;
    }
  };

  // Swipe support with animation
  const typeOrder = useMemo(() => {
    const types = ['products', 'services'];
    if (vouchers.length > 0) types.push('vouchers');
    return types;
  }, [vouchers]);

  const [swipeAnim, setSwipeAnim] = useState(null); // 'left' | 'right' | null
  const touchRef = useRef({ startX: 0, startY: 0 });

  const handleTouchStart = useCallback((e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
    const currentIdx = typeOrder.indexOf(activeType);
    if (dx < 0 && currentIdx < typeOrder.length - 1) {
      setSwipeAnim('left');
      setTimeout(() => { handleTypeChange(typeOrder[currentIdx + 1]); setSwipeAnim(null); }, 200);
    } else if (dx > 0 && currentIdx > 0) {
      setSwipeAnim('right');
      setTimeout(() => { handleTypeChange(typeOrder[currentIdx - 1]); setSwipeAnim(null); }, 200);
    }
  }, [activeType, typeOrder, handleTypeChange]);

  return (
    <div className="h_n pos-page-wrapper">
      <div className="body_container">

        <div className="pos-page">
          {/* Header: hamburger + type tabs */}
          <div className="catalogue-top-nav pos-top-nav">
            <span onClick={() => document.body.classList.toggle('nav_menu_open')} className="pos-menu-toggle">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </span>
            <div className="catalogue-top-nav-container">
              <div className="catalogue-top-nav-content">
                {!isMobile ? (
                  <nav className={`catalogue-tabs-nav ${loading ? 'catalogue-tabs-nav--loading' : ''}`}>
                    <button className={`catalogue-tab-btn ${activeType === 'products' ? 'active' : ''}`} onClick={() => handleTypeChange('products')}>
                      <span>Products</span>
                    </button>
                    <button className={`catalogue-tab-btn ${activeType === 'services' ? 'active' : ''}`} onClick={() => handleTypeChange('services')}>
                      <span>Services</span>
                    </button>
                    {!loading && vouchers.length > 0 && (
                      <button className={`catalogue-tab-btn ${activeType === 'vouchers' ? 'active' : ''}`} onClick={() => handleTypeChange('vouchers')}>
                        <span>Vouchers</span>
                      </button>
                    )}
                  </nav>
                ) : (
                  staffList.length > 0 && (
                    <StaffSelector
                      staffList={staffList}
                      selectedStaff={selectedStaff}
                      onSelect={(staff) => {
                        setSelectedStaff(staff);
                        if (state.items.length > 0) actions.setStaff(staff);
                      }}
                    />
                  )
                )}
              </div>
            </div>
            {!isMobile && (
              <div className="notif-bell-pinned-wrap">
                <LocationPicker />
                <NotificationBell />
              </div>
            )}
          </div>

          <div className="pos-layout">
            {/* Left: Search + Sub-tabs + Content */}
            <div
              className={`pos-grid-panel ${swipeAnim === 'left' ? 'pos-swipe-out-left' : swipeAnim === 'right' ? 'pos-swipe-out-right' : 'pos-swipe-in'}`}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <POSHeader
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                salonName={salonName}
              />

              {!isMobile ? (
                staffList.length > 0 && (
                  <StaffSelector
                    staffList={staffList}
                    selectedStaff={selectedStaff}
                    onSelect={(staff) => {
                      setSelectedStaff(staff);
                      if (state.items.length > 0) actions.setStaff(staff);
                    }}
                  />
                )
              ) : (
                <nav className={`catalogue-tabs-nav pos-mobile-type-tabs ${loading ? 'catalogue-tabs-nav--loading' : ''}`}>
                  <button className={`catalogue-tab-btn ${activeType === 'products' ? 'active' : ''}`} onClick={() => handleTypeChange('products')}>
                    <span>Products</span>
                  </button>
                  <button className={`catalogue-tab-btn ${activeType === 'services' ? 'active' : ''}`} onClick={() => handleTypeChange('services')}>
                    <span>Services</span>
                  </button>
                  {!loading && vouchers.length > 0 && (
                    <button className={`catalogue-tab-btn ${activeType === 'vouchers' ? 'active' : ''}`} onClick={() => handleTypeChange('vouchers')}>
                      <span>Vouchers</span>
                    </button>
                  )}
                </nav>
              )}

              <ProductGrid
                products={filteredProducts}
                serviceCategories={filteredServiceCategories}
                featuredServices={featuredServices}
                vouchers={filteredVouchers}
                activeType={activeType}
                activeSubCategory={activeSubCategory}
                onSubCategoryChange={setActiveSubCategory}
                productCategories={visibleCategories}
                serviceCategoryTabs={serviceCategoryTabs}
                onAddProduct={handleAddProduct}
                onAddService={handleAddService}
                lastAddedId={lastAddedId}
                onAddVoucher={handleAddVoucher}
                loading={loading}
                currencySymbol={currencySymbol}
                searchQuery={searchQuery}
                hasVouchers={vouchers.length > 0}
              />
            </div>

            {/* Desktop: always-visible side cart panel */}
            <div className="pos-cart-panel pos-cart-panel--desktop">
              {pickerVoucher && !isMobile ? renderVoucherAmountStep() : renderCartScreen()}
            </div>
          </div>

        </div>
      </div>

      {/* Voucher amount — MOBILE only: bottom sheet (slide-up), rendered outside
          pos-page so its fixed positioning isn't captured by an ancestor
          transform. Desktop renders it inline in the right cart panel. */}
      {pickerVoucher && isMobile && (
        <VoucherAmountPicker
          voucher={pickerVoucher}
          currency={currencySymbol}
          onConfirm={handleVoucherAmountConfirmed}
          onClose={() => setPickerVoucher(null)}
        />
      )}

      {/* Mobile: Cart summary bar (outside pos-page to avoid transform containment) */}
      {state.items.length > 0 && !cartOpen && isMobile && (
        <div className={`pos-cart-bar ${cartPulse ? 'pos-cart-bar--pulse' : ''}`} onClick={() => setCartOpen(true)}>
          <span className="pos-cart-bar-count">{state.items.length} item{state.items.length !== 1 ? 's' : ''}</span>
          <span className="pos-cart-bar-dot">·</span>
          <span className="pos-cart-bar-total">{currencySymbol}{(Number(state.total) || 0).toFixed(2)}</span>
          <span className="pos-cart-bar-action">Pay →</span>
        </div>
      )}

      {/* Mobile: SlidePanel cart */}
      {isMobile && cartOpen && (
        <SlidePanel ref={cartSlidePanelRef} onClose={() => setCartOpen(false)} className="pos-cart-sp">
          {renderCartScreen()}
        </SlidePanel>
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const { user } = state;
  return { user };
}

export default connect(mapStateToProps)(POSPage);
